import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import {
  Button,
  Card,
  ConfirmModal,
  LoadError,
  Modal,
  ModalActions,
  Pagination,
  RefreshControls,
} from '~/components/ui';
import { FormField, Select } from '~/components/form';
import { StatTile, StatTileSkeleton, StatsRow } from '~/components/dashboard';
import { useAutoRefresh } from '~/hooks/useAutoRefresh';
import { useListUrlState } from '~/hooks/useListUrlState';
import { clampPage, pageCount, splitValues } from '~/lib/listParams';
import { matchesServiceFilters, matchesServiceSearch } from '~/lib/serviceFilters';
import { queryErrorMessage } from '~/modules/query';
import {
  useGetDistinctHostsQuery,
  useGetServicesQuery,
  useGetServiceStatsQuery,
  useRunQuickActionMutation,
} from '~/modules/query/services';
import ServicesDrawer, { SERVICE_FILTER_FIELDS } from '~/components/pages/services/ServicesDrawer';
import ServicesTable from '~/components/pages/services/ServicesTable';
import {
  SERVICE_ACTIONS,
  SERVICE_STATS_TITLES,
  serviceActionReason,
  type PendingAction,
  type ServiceRow,
} from '~/components/Constants/serviceConstants';

/** Update edits one service on its own page, so the bulk bar drops it. Move is a host picker (DESIGN.md §12). */
const BULK_ACTIONS = SERVICE_ACTIONS.filter((a) => a.value !== 'Update');

const FILTER_KEYS = SERVICE_FILTER_FIELDS.map((field) => field.param);

/** eventType -> the API action and the past-tense words its success toast uses. */
const ACTIONS: Record<string, { action: string; done: string }> = {
  Start: { action: 'start', done: 'started' },
  Stop: { action: 'stop', done: 'stopped' },
  Rebuild: { action: 'rebuild', done: 'rebuilt' },
  Move: { action: 'move', done: 'moved' },
  Remove: { action: 'remove', done: 'deleted' },
  Acknowledgement: { action: 'acknowledge', done: 'acknowledged' },
};

export const Services = () => {
  const list = useListUrlState({ filterKeys: FILTER_KEYS });
  const {
    data: serviceList,
    isLoading: listLoading,
    isFetching: listFetching,
    fulfilledTimeStamp: listStamp,
    error: listError,
    refetch: refetchList,
  } = useGetServicesQuery();
  const {
    data: serviceStats,
    isLoading: statsLoading,
    isFetching: statsFetching,
    fulfilledTimeStamp: statsStamp,
    error: statsError,
    refetch: refetchStats,
  } = useGetServiceStatsQuery();
  const refreshAll = () => {
    refetchList();
    refetchStats();
  };
  const { auto, setAuto } = useAutoRefresh('services', refreshAll);
  const [runQuickAction] = useRunQuickActionMutation();
  const services = useMemo(() => (serviceList?.data ?? []) as ServiceRow[], [serviceList]);

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [moveHost, setMoveHost] = useState('');
  const [busy, setBusy] = useState(false);

  const filteredRows = useMemo(() => {
    const chosen = Object.fromEntries(
      SERVICE_FILTER_FIELDS.map(({ param }) => [param, splitValues(list.filters[param])]),
    );
    return services.filter(
      (row) => matchesServiceFilters(row, chosen) && (!list.q || matchesServiceSearch(row, list.q)),
    );
  }, [services, list.filters, list.q]);

  const pages = pageCount(filteredRows.length, list.pageSize);
  const page = clampPage(list.page, filteredRows.length, list.pageSize);
  const pageRows = filteredRows.slice((page - 1) * list.pageSize, page * list.pageSize);

  // Bulk acts on the visible page only (Delete is destructive): ids kept from another page or filter are ignored.
  const selectedRows = pageRows.filter((row) => selectedIds.has(row._id));
  const selectedKeys = useMemo(() => new Set(selectedRows.map((row) => row._id)), [selectedRows]);

  const hosts = useGetDistinctHostsQuery(undefined, { skip: selectedRows.length === 0 });
  // Offer every host except the one all selected services already run on.
  const hostOptions = useMemo(
    () =>
      (hosts.data?.distinctHosts ?? [])
        .filter((host) => !selectedRows.every((row) => row.host && host.includes(row.host)))
        .map((host) => ({ label: host, value: host })),
    [hosts.data, selectedRows],
  );
  const noOtherHost = hosts.isSuccess && hostOptions.length === 0;
  const chosenHost = moveHost || hostOptions[0]?.value || '';

  const eventType = pending?.eventType;
  const rows = pending?.rows ?? [];
  const only = rows.length === 1 ? rows[0] : undefined;

  const toggleRow = (key: string) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const toggleAll = (checked: boolean) =>
    setSelectedIds(checked ? new Set(pageRows.map((row) => row._id)) : new Set());

  const subject = rows.length === 1 ? (only?.name ?? 'this service') : `${rows.length} services`;

  const run = () => {
    if (!pending) return Promise.resolve();
    const { action, done } = ACTIONS[pending.eventType];
    setBusy(true);
    return runQuickAction({
      action,
      ids: pending.rows.map((row) => row._id).join(','),
      ...(pending.eventType === 'Move' ? { newHost: chosenHost } : {}),
    })
      .unwrap()
      .then(() => {
        toast.success(
          pending.eventType === 'Acknowledgement'
            ? 'Issue acknowledged'
            : `${rows.length === 1 ? 'Service' : `${rows.length} services`} ${done}`,
        );
        setSelectedIds(new Set());
      })
      .catch((err) => {
        toast.error(
          queryErrorMessage(err) ?? 'The service action could not be completed. Try again.',
        );
      })
      .finally(() => {
        setBusy(false);
        setPending(null);
      });
  };

  const bulk =
    selectedRows.length > 0 ? (
      <div className="flex w-full flex-wrap items-center gap-3">
        <div className="font-medium text-gray-800 text-sm dark:text-white/90">
          {selectedRows.length} selected on this page
        </div>
        <div className="flex flex-wrap gap-2">
          {BULK_ACTIONS.map((option) => {
            const reason =
              option.value === 'Move'
                ? noOtherHost
                  ? 'There is no other host to move these services to.'
                  : hosts.isSuccess
                    ? undefined
                    : 'Loading the host list.'
                : selectedRows.every((row) => serviceActionReason(option.value, row))
                  ? serviceActionReason(option.value, selectedRows[0])
                  : undefined;
            return (
              <Button
                key={option.value}
                variant="outline"
                startIcon={option.icon}
                disabled={reason !== undefined}
                title={reason}
                testId={`services-bulk-${option.value.toLowerCase()}`}
                onClick={() => {
                  setMoveHost('');
                  setPending({ eventType: option.value, rows: selectedRows });
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      </div>
    ) : undefined;

  const confirmLabel = eventType === 'Acknowledgement' ? 'Acknowledge issue' : eventType;

  return (
    <PageShell>
      <PageHeader
        title="Services"
        breadcrumbs={[{ label: 'Services' }]}
        extra={
          <RefreshControls
            onRefresh={refreshAll}
            fetching={listFetching || statsFetching}
            updatedAt={listStamp && statsStamp ? Math.min(listStamp, statsStamp) : undefined}
            auto={auto}
            onAutoChange={setAuto}
            subject="services"
          />
        }
      />

      {statsError && (
        <LoadError
          what="the service stats"
          message={queryErrorMessage(statsError)}
          onRetry={refetchStats}
          testId="services-stats"
        />
      )}

      <StatsRow ariaLabel="Service statistics">
        {Object.keys(SERVICE_STATS_TITLES).map((key) =>
          statsLoading ? (
            <StatTileSkeleton key={key} />
          ) : (
            <StatTile
              key={key}
              label={SERVICE_STATS_TITLES[key]}
              value={serviceStats?.[key] as string | number | null | undefined}
            />
          ),
        )}
      </StatsRow>

      <Card>
        <ListToolbar
          title="Services"
          bulk={bulk}
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search services…',
            testId: 'services-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setShowFilterDrawer(true),
            onClear: list.clear,
            testId: 'services-filter-button',
          }}
        />
        <ServicesTable
          data={pageRows}
          loading={listLoading}
          refreshing={listFetching && !listLoading}
          error={
            listError
              ? {
                  what: 'the services',
                  message: queryErrorMessage(listError),
                  onRetry: refetchList,
                }
              : undefined
          }
          filtered={Boolean(list.q) || list.filterCount > 0}
          selectedKeys={selectedKeys}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          onAction={(type, row) => setPending({ eventType: type, rows: [row] })}
        />
        <Pagination
          pageIndex={page - 1}
          pageSize={list.pageSize}
          pageCount={pages}
          totalRows={filteredRows.length}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <ServicesDrawer
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        value={list.filters}
        onApply={list.setFilters}
        onClear={list.clear}
        rows={services}
      />

      <ConfirmModal
        isOpen={eventType !== undefined && eventType !== 'Remove' && eventType !== 'Move'}
        title={confirmLabel ?? ''}
        message={
          <>
            {eventType === 'Acknowledgement' ? 'Acknowledge the issue on' : eventType}{' '}
            <strong>{subject}</strong>?
          </>
        }
        confirmLabel={confirmLabel}
        testId="services-action-confirm"
        onConfirm={run}
        onClose={() => setPending(null)}
      />

      <ConfirmModal
        isOpen={eventType === 'Remove'}
        variant="danger"
        title={`Delete ${subject}`}
        message={
          <>
            Delete <strong>{subject}</strong>?
          </>
        }
        dangerNote="This removes the container from its host and cannot be undone."
        confirmLabel="Delete"
        testId="services-remove-confirm"
        onConfirm={run}
        onClose={() => setPending(null)}
      />

      <Modal
        isOpen={eventType === 'Move'}
        onClose={() => setPending(null)}
        size="small"
        title={`Move ${subject}`}
        footer={
          <ModalActions onDismiss={() => setPending(null)} dismissLabel="Cancel">
            <Button size="sm" loading={busy} disabled={!chosenHost} onClick={run}>
              Move
            </Button>
          </ModalActions>
        }
      >
        <FormField id="services-move-host" label="Move services to">
          <Select
            id="services-move-host"
            testId="services-move-host"
            ariaLabel="Move services to"
            options={hostOptions}
            value={chosenHost}
            onChange={setMoveHost}
          />
        </FormField>
      </Modal>
    </PageShell>
  );
};
